import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedAsset } from '@app/core/model/entities/asset/asset';
import { Contract, ContractInput } from '@app/core/model/entities/asset/contract';
import { Space } from '@app/core/model/entities/asset/space';
import { Field } from '@app/core/model/other/field-config';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale, isNullOrEmpty } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import { TranslateService } from '@ngx-translate/core';
import { FieldService } from '@services/field.service';
import { AccessManager } from '@services/managers/access.manager';
import { GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { BehaviorSubject, EMPTY, from, Observable, of } from 'rxjs';
import { filter, mergeMap, switchMap, toArray } from 'rxjs/operators';

@Component({
    templateUrl: './organization-contract-renew-modal.component.html',
    styleUrl: '../../../../../../shared/components/form-builder/sections/create-modal.component.scss',
    standalone: false
})
export class OrganizationContractRenewModalComponent implements OnInit, GeneratesObject {
  private contractToRenew = inject<{ contractToRenew: Contract; }>(MAT_DIALOG_DATA).contractToRenew;
  public appConfig = inject(AppConfig);

  public gridApi: GridApi;
  public gridOptions: GridOptions;

  private spaces$ = new BehaviorSubject<Space[]>([]);

  protected formStateService = inject(FormStateService);
  protected assetTypeAllowsSpaceModule = false;
  protected gridOptionsService = inject(GridOptionsService);
  protected assetsService = inject(AssetsService);
  protected spacesService = inject(SpacesService);
  protected columnBuilder = inject(ColumnBuilder);
  protected fb = inject(FormBuilder);
  private fieldService = inject(FieldService);
  private accessManager = inject(AccessManager);
  protected destroyRef = inject(DestroyRef);

  public contractForm: FormGroup<{
    provider: FormControl<string>,
    asset: FormControl<RelatedAsset>,
    spaces: FormControl<Space[]>
  }>;
  protected missingRequiredFields$: Observable<Field[]>;

  private invalidRows: string[] = [];

  protected translate: TranslateService = inject(TranslateService);

  constructor() {
    this.gridOptions = {
      ...this.gridOptionsService.staticSelectSpacesGridOptions,
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      localeText: this.columnBuilder.localeTextCommon(),
      domLayout: 'autoHeight',
      rowClassRules: {
        'invalid-row': ({data}: { data: Space }): boolean => this.invalidRows.includes(data.id)
      },
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this)
    };

    this.contractForm = this.fb.group({
      provider: this.fb.control<string>('', Validators.compose([
        Validators.required,
        Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)
      ])),
      asset: this.fb.control(null, Validators.required),
      spaces: this.fb.control([], Validators.compose([
        ExtraValidators.checkSpaces
      ]))
    });

    // All fields with a value in the contract to renew
    const contractExistingFields = Object.entries(this.contractToRenew)
      .filter(([_, value]) => !isNullOrEmpty(value))
      .map(([key]) => key)
      .concat(Object.keys(this.contractToRenew.properties));

    this.missingRequiredFields$ = this.fieldService.getFields({computed: 'ALL', entityType: EntityTypeEnum.CONTRACT}).pipe(
      mergeMap(fields => from(fields)),
      filter(field => !field.computed && contractExistingFields.includes(field.code)),
      // Keep remaining fields without sufficient user permissions
      filter(({code}) => !this.accessManager.hasFieldWriteOnCreateAccess(code, EntityTypeEnum.CONTRACT)),
      toArray(),
      takeUntilDestroyed()
    );
  }


  /**
   * Load the grid's column definitions and fill with the selected Asset's Spaces.
   * @param params Ag-grid params.
   */
  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.applyColumnState({
      state: [
        {colId: 'ag-Grid-AutoColumn', sort: 'asc'}
      ]
    });

    // Resize columns when receiving new Spaces
    this.spaces$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(spaces => {
        this.gridApi.setGridOption('rowData', spaces);
        this.gridApi.sizeColumnsToFit();
        this.contractForm.controls.spaces.reset([]);
      });
  }

  /**
   * Update value in the form and refresh invalid rows.
   * @param event Event thrown by the grid.
   */
  private onRowSelected(event: RowSelectedEvent): void {
    const spaces = this.gridApi.getSelectedRows() as Space[];
    this.contractForm.controls.spaces.setValue(spaces);
    this.contractForm.controls.spaces.markAsTouched();

    const nodesToRefresh = [event.node];

    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }

    // Refresh rows
    this.gridApi.redrawRows({
      rowNodes: nodesToRefresh
    });
  }

  /**
   * Set the form's initial values, disable fields that can not be edited when renewing a Contract.
   */
  public ngOnInit(): void {

    // Set form value for Contract's provider and disable input
    this.contractForm.controls.provider.setValue(this.contractToRenew.provider);
    this.contractForm.controls.provider.disable();

    // Listen for Spaces to be displayed in the grid
    this.contractForm.controls.asset.valueChanges
      .pipe(
        switchMap((relatedAsset?: RelatedAsset) => {
          this.assetTypeAllowsSpaceModule = relatedAsset?.typeModules.includes(EntityTypeEnum.SPACE);
          if (this.assetTypeAllowsSpaceModule) {
            this.contractForm.controls.spaces.addValidators(Validators.required);
            const selectParams: SelectParams = {
              locale: getLocale(),
              filters: [
                eq('assetId', relatedAsset.id)
              ]
            };
            return this.spacesService.loadAllSpaces(selectParams);
          }
          this.contractForm.controls.spaces.removeValidators(Validators.required);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.spaces$);

    // Find Asset linked to the Contract and set form value
    this.contractForm.controls.asset.setValue(
      new RelatedAsset(this.contractToRenew.computedProperties.asset)
    );
  }

  /**
   * Get the form's data for creating a new Contract.
   * @return New Contract's data.
   */
  public getGeneratedObject(): ContractInput {
    const {provider, spaces} = this.contractForm.getRawValue();
    return {
      provider,
      spaceIds: spaces.map(space => space.id)
    };
  }

  /**
   * Return empty Observable. This override method does not fetch accessible Assets from the
   * API as it is not necessary in that case.
   * @return Empty Observable.
   * @protected
   */
  protected fetchAssets(): Observable<RelatedAsset[]> {
    return EMPTY;
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return true if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows().some(selectedRow =>
      space.parentPath.includes(selectedRow.id)
    );
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return true is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows().some(selectedRow =>
      selectedRow.parentPath.includes(space.id)
    );
  }
}
